/**
 * KTP Tab Component
 * 
 * Allows users to view and update their KTP (Kartu Tanda Penduduk) information.
 */

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AlertCircle, CheckCircle2, CreditCard, Info, Upload, Loader2 } from "lucide-react";
import { authService } from "@/services/authService";
import type { User } from "@/services/authService";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { apiConfig } from "@/config/api";
import { apiFetch, UPLOAD_API_TIMEOUT_MS } from "@/lib/apiFetch";
import { PersonalDataUploadConsentDialog } from "@/components/common/PersonalDataUploadConsentDialog";

interface KTPTabProps {
  user: User;
  /** When false (super admin viewing another user), idcard loads from /api/users/:id and edits are hidden. */
  allowEdit?: boolean;
}

export function KTPTab({ user, allowEdit = true }: KTPTabProps) {
  const { refreshUser } = useAuth();
  const [ktp, setKtp] = React.useState(user.ktp || "");
  const [loading, setLoading] = React.useState(false);
  const [errors, setErrors] = React.useState<{
    ktp?: string;
    general?: string;
  }>({});
  const [validationStatus, setValidationStatus] = React.useState<{
    isValid: boolean | null;
    errors: string[];
  } | null>(null);

  // KTP photo upload state
  const [idcardFile, setIdcardFile] = React.useState<File | null>(null);
  const [idcardPreview, setIdcardPreview] = React.useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const [idcardBlobUrl, setIdcardBlobUrl] = React.useState<string | null>(null);
  const [idcardBlobLoading, setIdcardBlobLoading] = React.useState(false);
  const [consentOpen, setConsentOpen] = React.useState(false);

  React.useEffect(() => {
    if (!user.idcardEncrypted) {
      setIdcardBlobUrl(null);
      setIdcardBlobLoading(false);
      return;
    }

    let revoked = false;
    let objectUrl: string | null = null;

    const uid = user.id ? String(user.id).trim() : "";
    if (!uid) {
      setIdcardBlobLoading(false);
      return;
    }

    setIdcardBlobLoading(true);
    // Always use per-user URL: owner passes viewerId===subjectId; super admin passes requireUserLevel3. Avoids /api/auth mixing subjects when ids differ.
    const idcardUrl = `${apiConfig.baseURL}/api/users/${encodeURIComponent(uid)}/ktp-idcard`;

    fetch(idcardUrl, { credentials: "include" })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.blob();
      })
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        if (revoked) {
          URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        setIdcardBlobUrl(u);
      })
      .catch(() => {
        if (!revoked) {
          setIdcardBlobUrl(null);
        }
      })
      .finally(() => {
        if (!revoked) {
          setIdcardBlobLoading(false);
        }
      });

    return () => {
      revoked = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      setIdcardBlobUrl(null);
    };
  }, [user.idcardEncrypted, user.id]);

  // Initialize validation status from user data
  React.useEffect(() => {
    if (user.ktp) {
      // If user has isValidKTP field, use it
      if (user.isValidKTP !== undefined && user.isValidKTP !== null) {
        setValidationStatus({
          isValid: user.isValidKTP,
          errors: user.isValidKTP ? [] : ["KTP tidak valid"],
        });
      }
    }
  }, [user.ktp, user.isValidKTP]);

  // Validate KTP format
  const validateKTPFormat = (ktpValue: string): string | null => {
    const trimmed = ktpValue.trim();
    
    if (!trimmed) {
      return null; // Empty is allowed (user can clear KTP)
    }

    if (trimmed.length < 16) {
      return `KTP harus terdiri dari 16 digit (saat ini: ${trimmed.length} digit)`;
    }

    if (trimmed.length > 16) {
      return `KTP harus terdiri dari 16 digit (saat ini: ${trimmed.length} digit)`;
    }

    if (!/^\d+$/.test(trimmed)) {
      return "KTP harus berupa angka saja";
    }

    return null;
  };

  // Validate KTP on input change
  const handleKTPChange = (value: string) => {
    // Only allow digits
    const digitsOnly = value.replace(/\D/g, "");
    setKtp(digitsOnly);

    // Clear errors when user types
    if (errors.ktp) {
      setErrors({ ...errors, ktp: undefined });
    }

    // Validate format
    if (digitsOnly.length > 0) {
      const formatError = validateKTPFormat(digitsOnly);
      if (formatError) {
        setValidationStatus({
          isValid: false,
          errors: [formatError],
        });
      } else {
        // Format is valid, but we don't know if it can generate date yet
        // Set to null to indicate needs server validation
        setValidationStatus(null);
      }
    } else {
      setValidationStatus(null);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate format
    if (ktp.trim()) {
      const formatError = validateKTPFormat(ktp);
      if (formatError) {
        setErrors({ ktp: formatError });
        return;
      }
    }

    setLoading(true);
    try {
      await authService.updateProfile({ ktp: ktp.trim() || null });
      
      toast.success("KTP berhasil diperbarui", {
        description: ktp.trim() 
          ? "Nomor KTP Anda telah berhasil diperbarui." 
          : "KTP telah dihapus dari profil Anda.",
      });

      // Refresh user data to get updated validation status
      await refreshUser();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Gagal memperbarui KTP";
      setErrors({ general: errorMessage });
      toast.error("Gagal memperbarui KTP", {
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle KTP photo file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran file maksimal 5MB");
      return;
    }

    setIdcardFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setIdcardPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const performIdcardUpload = async () => {
    if (!idcardFile) return;

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("idcardUrl", idcardFile);

      const res = await apiFetch(`${apiConfig.baseURL}/api/auth/upload-photos`, {
        method: "POST",
        credentials: "include",
        body: formData,
        timeoutMs: UPLOAD_API_TIMEOUT_MS,
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Gagal mengupload foto KTP");
      }

      toast.success("Foto KTP berhasil diupload");
      setIdcardFile(null);
      setIdcardPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await refreshUser();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Gagal mengupload foto KTP";
      toast.error(msg);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleUploadPhotoClick = () => {
    if (!idcardFile) return;
    setConsentOpen(true);
  };

  // Get validation status display
  const getValidationDisplay = () => {
    if (!ktp.trim()) {
      return null;
    }

    if (validationStatus === null) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Info className="h-4 w-4" />
          <span>Masukkan 16 digit nomor KTP</span>
        </div>
      );
    }

    if (validationStatus.isValid) {
      return (
        <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
          <CheckCircle2 className="h-4 w-4" />
          <span>KTP valid</span>
        </div>
      );
    }

    return (
      <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-medium">KTP tidak valid</p>
          {validationStatus.errors.length > 0 && (
            <ul className="mt-1 list-disc list-inside text-xs">
              {validationStatus.errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  const legacyIdcardSrc = user.idcardUrl
    ? user.idcardUrl.startsWith("data:") || user.idcardUrl.startsWith("http")
      ? user.idcardUrl
      : `data:image/jpeg;base64,${user.idcardUrl}`
    : null;
  const displayedIdcardSrc =
    !idcardPreview && (user.idcardEncrypted ? idcardBlobUrl : legacyIdcardSrc);

  const readOnlyKtpDisplay =
    user.ktp && user.ktp.length >= 4
      ? `${user.ktp.substring(0, 2)}****${user.ktp.substring(user.ktp.length - 2)}`
      : user.ktp
        ? "****"
        : "—";

  return (
    <div className="space-y-4">
      {/* KTP Photo Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Foto KTP
          </CardTitle>
          <CardDescription>
            {allowEdit
              ? "Upload atau perbarui foto Kartu Tanda Penduduk (KTP) Anda"
              : "Foto KTP anggota (hanya lihat)."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current KTP photo */}
          {!idcardPreview && user.idcardEncrypted && idcardBlobLoading && (
            <p className="text-sm text-muted-foreground text-center">Memuat foto KTP…</p>
          )}
          {!idcardPreview && user.idcardEncrypted && !idcardBlobLoading && !idcardBlobUrl && (
            <p className="text-sm text-destructive text-center">
              Foto KTP tidak dapat dimuat. Coba muat ulang halaman atau upload ulang.
            </p>
          )}
          {displayedIdcardSrc && (
            <div className="flex justify-center">
              <img
                src={displayedIdcardSrc}
                alt="Foto KTP"
                className="max-w-md h-auto rounded-lg border border-input shadow-sm"
              />
            </div>
          )}

          {/* Preview selected photo */}
          {idcardPreview && (
            <div className="flex justify-center">
              <img
                src={idcardPreview}
                alt="Preview Foto KTP"
                className="max-w-md h-auto rounded-lg border border-input shadow-sm"
              />
            </div>
          )}

          {allowEdit && (
            <>
              <div className="space-y-2">
                <Label htmlFor="ktp-photo">Pilih Foto KTP</Label>
                <input
                  ref={fileInputRef}
                  id="ktp-photo"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:cursor-pointer"
                  disabled={uploadingPhoto}
                />
                <p className="text-xs text-muted-foreground">
                  Format: JPEG, PNG, atau WebP. Maksimal 5MB.
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleUploadPhotoClick}
                  disabled={!idcardFile || uploadingPhoto}
                >
                  {uploadingPhoto ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Mengupload...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload Foto KTP
                    </>
                  )}
                </Button>
                {idcardFile && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIdcardFile(null);
                      setIdcardPreview(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    disabled={uploadingPhoto}
                  >
                    Batal
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Nomor KTP
          </CardTitle>
          <CardDescription>
            {allowEdit
              ? "Update nomor Kartu Tanda Penduduk (KTP) Anda. KTP digunakan untuk ekstraksi informasi tanggal lahir."
              : "Data KTP anggota (hanya lihat). Pengubahan hanya dapat dilakukan oleh pemilik akun."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!allowEdit ? (
            <div className="rounded-md border border-input bg-muted/40 px-3 py-2 text-sm font-mono">
              {readOnlyKtpDisplay}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {errors.general && (
                <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <div>{errors.general}</div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="ktp">Nomor KTP</Label>
                <Input
                  id="ktp"
                  type="text"
                  inputMode="numeric"
                  value={ktp}
                  onChange={(e) => handleKTPChange(e.target.value)}
                  placeholder="Masukkan 16 digit nomor KTP"
                  disabled={loading}
                  maxLength={16}
                  className={errors.ktp ? "border-destructive" : ""}
                />
                {errors.ktp && (
                  <p className="text-sm text-destructive">{errors.ktp}</p>
                )}
                {getValidationDisplay()}
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    KTP harus terdiri dari 16 digit angka. Kosongkan untuk menghapus KTP.
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Tanggal lahir anda akan tertulis di KTA, kesalahan dalam penulisan nomer ktp akan mempengaruhi data yang ditampilkan, dan bukan tanggung jawab dari aplikasi.
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={loading || ktp === (user.ktp || "")}>
                  {loading ? "Menyimpan..." : "Simpan KTP"}
                </Button>
                {ktp && ktp !== (user.ktp || "") && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setKtp(user.ktp || "");
                      setErrors({});
                      setValidationStatus(null);
                    }}
                    disabled={loading}
                  >
                    Batal
                  </Button>
                )}
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {allowEdit ? (
        <PersonalDataUploadConsentDialog
          open={consentOpen}
          onOpenChange={setConsentOpen}
          onConfirm={() => {
            setConsentOpen(false);
            void performIdcardUpload();
          }}
          confirmLabel="Unggah foto KTP"
        />
      ) : null}
    </div>
  );
}

